import React from 'react';
import { ChevronRight, Home } from 'lucide-react';
import { createBreadcrumbSchema, type BreadcrumbItem } from '../seo/schemas.js';

export interface BreadcrumbNavProps {
  items: BreadcrumbItem[];
  className?: string;
  dark?: boolean;
}

/**
 * Reusable accessible Breadcrumb Navigation component that renders:
 * 1. Semantic HTML navigation with ARIA attributes & clean UI.
 * 2. Schema.org BreadcrumbList JSON-LD for search engine indexing.
 */
export const BreadcrumbNav: React.FC<BreadcrumbNavProps> = ({
  items,
  className = '',
  dark = false,
}) => {
  if (!items || items.length === 0) return null;

  const breadcrumbSchema = createBreadcrumbSchema(items);

  const textClass = dark ? 'text-slate-300 hover:text-white' : 'text-slate-500 hover:text-indigo-600';
  const activeClass = dark ? 'text-white font-semibold' : 'text-slate-900 font-semibold';
  const separatorClass = dark ? 'text-slate-600' : 'text-slate-400';

  return (
    <>
      {/* Schema.org BreadcrumbList JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <nav aria-label="Breadcrumb" className={`flex items-center text-xs sm:text-sm ${className}`}>
        <ol className="flex items-center flex-wrap gap-1.5 sm:gap-2">
          {items.map((item, index) => {
            const isLast = index === items.length - 1;
            const isFirst = index === 0;

            return (
              <li key={item.url || index} className="flex items-center gap-1.5 sm:gap-2">
                {index > 0 && <ChevronRight className={`w-3.5 h-3.5 shrink-0 ${separatorClass}`} aria-hidden="true" />}
                
                {isLast ? (
                  <span
                    className={`line-clamp-1 max-w-[200px] sm:max-w-[320px] ${activeClass}`}
                    aria-current="page"
                  >
                    {item.name}
                  </span>
                ) : (
                  <a
                    href={item.url}
                    className={`inline-flex items-center gap-1 transition-colors ${textClass}`}
                  >
                    {isFirst && <Home className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />}
                    <span>{item.name}</span>
                  </a>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
};
